import React, { useState, useCallback, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import { FaCloudUploadAlt, FaFile, FaTimes, FaCheck } from 'react-icons/fa';
import Modal from './Modal';
import { fileToBase64, isValidFileSize, formatFileSize } from '../utils/fileUtils';
import { toast } from 'react-toastify';
import { fileService } from '../services/api';

const MAX_TAGS = 6;

// Función para normalizar tags (igual que en el backend)
const normalizeTag = (tag) => {
  if (!tag || typeof tag !== 'string') return null;
  return tag.toLowerCase().trim().replace(/\s+/g, '-');
};

// Función para formatear tiempo restante
const formatTimeRemaining = (seconds) => {
  if (seconds < 60) {
    return `~${Math.round(seconds)} seg`;
  }
  const minutes = Math.floor(seconds / 60);
  const secs = Math.round(seconds % 60);
  if (secs === 0) {
    return `~${minutes} min`;
  }
  return `~${minutes} min ${secs} seg`;
};

// Función para convertir archivo a base64 con progreso simulado
const fileToBase64WithProgress = (file, onProgress) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    let progressInterval;
    
    // Simular progreso durante la conversión (0-50%)
    let simulatedProgress = 0;
    const startTime = Date.now();
    const estimatedTime = Math.max(1000, file.size / 10000); // Estimación basada en tamaño
    
    progressInterval = setInterval(() => {
      if (simulatedProgress < 50) {
        simulatedProgress += 2;
        const elapsed = (Date.now() - startTime) / 1000;
        const rate = simulatedProgress / elapsed;
        const remaining = (100 - simulatedProgress) / rate;
        
        if (onProgress) {
          onProgress(simulatedProgress, remaining);
        }
      }
    }, 50);
    
    reader.readAsDataURL(file);
    reader.onload = () => {
      clearInterval(progressInterval);
      // Remover el prefijo "data:type/subtype;base64,"
      const base64 = reader.result.split(',')[1];
      if (onProgress) {
        onProgress(50, 0); // 50% completado (conversión lista)
      }
      resolve(base64);
    };
    reader.onerror = error => {
      clearInterval(progressInterval);
      reject(error);
    };
  });
};

// Función para normalizar y validar rutas de archivos
const normalizeFilePath = (file, currentPath = '') => {
  // Validar que no tenga rutas relativas peligrosas
  if (file.webkitRelativePath && file.webkitRelativePath.includes('..')) {
    throw new Error('Rutas relativas (..) no permitidas por seguridad');
  }
  if (file.name && file.name.includes('..')) {
    throw new Error('Rutas relativas (..) no permitidas por seguridad');
  }
  
  // Si tiene webkitRelativePath, usar esa ruta completa (preserva estructura desde root)
  if (file.webkitRelativePath) {
    // Normalizar barras (Windows usa \)
    let filePath = file.webkitRelativePath.replace(/\\/g, '/');
    // Remover barras iniciales
    filePath = filePath.replace(/^\/+/, '');
    
    // Si currentPath está especificado y la ruta ya lo incluye, mantenerla
    // Si no, combinar con currentPath para preservar el contexto
    if (currentPath && !filePath.startsWith(currentPath)) {
      const normalizedCurrentPath = currentPath.endsWith('/') ? currentPath : currentPath + '/';
      return normalizedCurrentPath + filePath;
    }
    
    return filePath;
  }
  
  // Comportamiento actual: solo nombre del archivo
  // Combinar con currentPath si existe
  if (currentPath) {
    const normalizedCurrentPath = currentPath.endsWith('/') ? currentPath : currentPath + '/';
    return normalizedCurrentPath + file.name;
  }
  
  return file.name;
};

const UploadModal = ({ isOpen, onClose, onUpload, onUploadComplete, currentPath = '' }) => {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [selectedTags, setSelectedTags] = useState([]);
  const [predefinedTags, setPredefinedTags] = useState([]);
  const [loadingPredefined, setLoadingPredefined] = useState(false);
  const [uploadMode, setUploadMode] = useState('files'); // 'files' o 'folder'

  // Cargar tags predefinidos cuando se abre el modal
  useEffect(() => {
    if (isOpen) {
      loadPredefinedTags();
    }
  }, [isOpen]);

  // Limpiar archivos seleccionados cada vez que se abre el modal
  useEffect(() => {
    if (isOpen) {
      setFiles([]);
      setUploading(false);
      setSelectedTags([]);
      setUploadMode('files'); // Resetear a modo archivos por defecto
    }
  }, [isOpen]);

  const loadPredefinedTags = async () => {
    setLoadingPredefined(true);
    try {
      const tags = await fileService.getPredefinedTags();
      setPredefinedTags(tags || []);
    } catch (error) {
      console.error('Error cargando tags predefinidos:', error);
      toast.error('Error al cargar tags predefinidos', { autoClose: 2000 });
    } finally {
      setLoadingPredefined(false);
    }
  };

  const handleTagToggle = (tag) => {
    setSelectedTags(prev => {
      if (prev.includes(tag)) {
        // Remover tag
        return prev.filter(t => t !== tag);
      } else {
        // Agregar tag (máximo 6)
        if (prev.length >= MAX_TAGS) {
          toast.warning(`Solo se pueden seleccionar máximo ${MAX_TAGS} tags`, { autoClose: 2000 });
          return prev;
        }
        return [...prev, tag];
      }
    });
  };

  // Función personalizada para obtener archivos del evento y preservar webkitRelativePath
  const getFilesFromEvent = useCallback((event) => {
    return new Promise((resolve) => {
      let resolved = false;
      
      if (event.dataTransfer) {
        // Drag and drop
        const items = event.dataTransfer.items;
        if (items && items.length > 0) {
          const firstItem = items[0];
          if (firstItem.webkitGetAsEntry) {
            const entry = firstItem.webkitGetAsEntry();
            if (entry && entry.isDirectory) {
              // Es una carpeta arrastrada - leer estructura manualmente
              console.log('📁 Carpeta detectada, leyendo estructura...');
              
              const readDirectory = (entry, path = '') => {
                return new Promise((resolveDir) => {
                  const dirReader = entry.createReader();
                  const entries = [];
                  
                  const readEntries = () => {
                    dirReader.readEntries((items) => {
                      if (items.length === 0) {
                        resolveDir(entries);
                      } else {
                        entries.push(...items);
                        readEntries(); // Continuar leyendo
                      }
                    });
                  };
                  
                  readEntries();
                });
              };
              
              const processEntry = async (entry, path = '') => {
                const fullPath = path ? `${path}/${entry.name}` : entry.name;
                
                if (entry.isFile) {
                  return new Promise((resolveFile) => {
                    entry.file((file) => {
                      // Agregar webkitRelativePath manualmente
                      Object.defineProperty(file, 'webkitRelativePath', {
                        value: fullPath,
                        writable: false,
                        configurable: true
                      });
                      resolveFile(file);
                    });
                  });
                } else if (entry.isDirectory) {
                  const subEntries = await readDirectory(entry, fullPath);
                  const subFiles = await Promise.all(
                    subEntries.map(subEntry => processEntry(subEntry, fullPath))
                  );
                  return subFiles.flat();
                }
                return [];
              };
              
              processEntry(entry).then((allFiles) => {
                if (!resolved) {
                  resolved = true;
                  console.log(`✅ ${allFiles.length} archivos leídos de la carpeta con estructura preservada`);
                  resolve(allFiles);
                }
              }).catch((error) => {
                if (!resolved) {
                  resolved = true;
                  console.error('Error leyendo carpeta:', error);
                  // Fallback a archivos normales del dataTransfer
                  const fileList = event.dataTransfer.files;
                  resolve(Array.from(fileList));
                }
              });
              
              return; // Salir temprano si es carpeta
            }
          }
        }
        
        // Si no es carpeta o no se pudo detectar, usar archivos normales
        if (!resolved) {
          const fileList = event.dataTransfer.files;
          resolve(Array.from(fileList));
        }
      } else if (event.target && event.target.files) {
        // File input - estos ya tienen webkitRelativePath si webkitdirectory está activo
        resolve(Array.from(event.target.files));
      } else {
        resolve([]);
      }
    });
  }, []);

  const onDrop = useCallback((acceptedFiles, fileRejections, event) => {
    console.log('📁 Archivos recibidos:', acceptedFiles.length);
    if (acceptedFiles.length > 0) {
      console.log('📁 Primer archivo:', {
        name: acceptedFiles[0].name,
        webkitRelativePath: acceptedFiles[0].webkitRelativePath || 'NO',
        path: acceptedFiles[0].path || 'NO'
      });
    }
    
    const newFiles = acceptedFiles.map(file => {
      // Detectar si tiene webkitRelativePath (carpeta arrastrada)
      const hasFolderStructure = !!file.webkitRelativePath;
      let filePath = null;
      
      if (hasFolderStructure) {
        console.log(`📄 Archivo con estructura: ${file.name} -> ${file.webkitRelativePath}`);
      }
      
      try {
        // Normalizar la ruta para validación y display
        filePath = normalizeFilePath(file, currentPath);
        if (hasFolderStructure) {
          console.log(`✅ Ruta normalizada: ${filePath}`);
        }
      } catch (error) {
        console.warn('Error normalizando ruta:', error.message);
        // Si hay error, usar solo el nombre del archivo
        filePath = currentPath 
          ? (currentPath.endsWith('/') ? currentPath : currentPath + '/') + file.name
          : file.name;
      }
      
      return {
        file,
        id: Date.now() + Math.random(),
        status: 'pending', // pending, uploading, success, error
        filePath, // Ruta completa normalizada
        hasFolderStructure, // Indica si viene de una carpeta arrastrada
        progress: 0, // 0-100
        timeRemaining: 0 // segundos restantes
      };
    });
    setFiles(prev => [...prev, ...newFiles]);
  }, [currentPath]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: true,
    getFilesFromEvent: getFilesFromEvent,
    // Permitir seleccionar carpetas también
    noClick: false,
    noKeyboard: false
  });
  
  // Modificar el input según el modo de subida
  const inputProps = getInputProps();
  const modifiedInputProps = {
    ...inputProps,
    multiple: true,
    // Solo agregar webkitdirectory si el modo es 'folder'
    ...(uploadMode === 'folder' ? { webkitdirectory: '', directory: '' } : {})
  };


  const removeFile = (fileId) => {
    setFiles(prev => prev.filter(f => f.id !== fileId));
  };

  const uploadFiles = async () => {
    if (files.length === 0) return;

    setUploading(true);
    // Normalizar los tags seleccionados antes de enviarlos
    const normalizedTags = selectedTags.map(tag => normalizeTag(tag)).filter(tag => tag);

    let successCount = 0;
    let errorCount = 0;
    
    for (let fileItem of files) {
      if (fileItem.status !== 'pending') continue;

      try {
        setFiles(prev => prev.map(f => 
          f.id === fileItem.id ? { ...f, status: 'uploading' } : f
        ));

        if (!isValidFileSize(fileItem.file, 500)) {
          throw new Error('El archivo es demasiado grande (máximo 500MB)');
        }

        // Callback para actualizar progreso
        const updateProgress = (progress, timeRemaining) => {
          setFiles(prev => prev.map(f => 
            f.id === fileItem.id 
              ? { ...f, progress: Math.min(100, Math.max(0, progress)), timeRemaining: Math.max(0, timeRemaining) }
              : f
          ));
        };

        // Convertir a base64 con progreso simulado (0-50%)
        const base64Data = await fileToBase64WithProgress(fileItem.file, (progress, timeRemaining) => {
          updateProgress(progress, timeRemaining);
        });
        
        // Usar la ruta normalizada si está disponible, sino usar file.name
        const fileNameToUpload = fileItem.filePath || fileItem.file.name;
        
        // Subir con progreso real del envío HTTP (50-100%)
        const success = await onUpload(
          fileNameToUpload, 
          base64Data, 
          fileItem.file.type,
          normalizedTags.length > 0 ? normalizedTags : null,
          (progress, timeRemaining) => {
            updateProgress(progress, timeRemaining);
          }
        );

        if (success) {
          setFiles(prev => prev.map(f => 
            f.id === fileItem.id 
              ? { ...f, status: 'success', progress: 100, timeRemaining: 0 } 
              : f
          ));
          successCount++;
        } else {
          throw new Error('Error al subir el archivo');
        }
      } catch (error) {
        setFiles(prev => prev.map(f => 
          f.id === fileItem.id ? { ...f, status: 'error', error: error.message } : f
        ));
        errorCount++;
      }
    }

    setUploading(false);

    // Mostrar mensajes solo cuando TODOS los archivos hayan terminado
    if (successCount > 0) {
      toast.success(`${successCount} archivo(s) subido(s) con éxito`, { autoClose: 3000 });
    }
    if (errorCount > 0) {
      toast.error(`${errorCount} archivo(s) no se pudieron subir`, { autoClose: 3000 });
    }
    
    // Solo cerrar si todos los archivos se subieron exitosamente
    // Si hay errores, dejar el modal abierto para que el usuario pueda ver qué falló
    const totalProcessed = successCount + errorCount;
    const allSuccessNoErrors = totalProcessed === files.length && errorCount === 0;
    
    if (allSuccessNoErrors) {
      // Esperar un momento para que el usuario vea el estado final
      setTimeout(() => {
        handleClose();
        // Recargar archivos después de cerrar el modal
        if (onUploadComplete) {
          onUploadComplete();
        }
      }, 1500);
    }
  };

  const handleClose = () => {
    setFiles([]);
    setUploading(false);
    setSelectedTags([]);
    onClose();
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'uploading':
        return <div className="animate-spin w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full" />;
      case 'success':
        return <FaCheck className="text-green-500" />;
      case 'error':
        return <FaTimes className="text-red-500" />;
      default:
        return <FaFile className="text-gray-400" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'uploading':
        return 'bg-blue-900 border-blue-600';
      case 'success':
        return 'bg-green-900 border-green-600';
      case 'error':
        return 'bg-red-900 border-red-600';
      default:
        return 'bg-gray-800 border-gray-600';
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Subir Archivos" size="lg">
      <div className="space-y-6">
        {/* Toggle para modo de subida */}
        <div className="flex items-center space-x-4 pb-4 border-b border-gray-700">
          <label className="text-sm font-medium text-gray-300">Modo de subida:</label>
          <div className="flex space-x-2">
            <button
              type="button"
              onClick={() => {
                setUploadMode('files');
                setFiles([]); // Limpiar archivos al cambiar modo
              }}
              disabled={uploading || files.length > 0}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                uploadMode === 'files'
                  ? 'bg-blue-600 text-white shadow-lg'
                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              } ${uploading || files.length > 0 ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
            >
              Archivos Individuales
            </button>
            <button
              type="button"
              onClick={() => {
                setUploadMode('folder');
                setFiles([]); // Limpiar archivos al cambiar modo
              }}
              disabled={uploading || files.length > 0}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                uploadMode === 'folder'
                  ? 'bg-blue-600 text-white shadow-lg'
                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              } ${uploading || files.length > 0 ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
            >
              Carpeta Completa
            </button>
          </div>
          <span className="text-xs text-gray-400 ml-2">
            {uploadMode === 'files' 
              ? 'Selecciona archivos individuales' 
              : 'Selecciona una carpeta completa'}
          </span>
        </div>

        {/* Tags Selection */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-300">
            Etiquetas (opcional, máx. {MAX_TAGS}) - Se aplicarán a todos los archivos
          </label>
          
          {loadingPredefined ? (
            <div className="text-sm text-gray-400">Cargando tags disponibles...</div>
          ) : (
            <>
              <div className="max-h-48 overflow-y-auto border border-gray-600 rounded-md p-4 bg-gray-800">
                <div className="flex flex-wrap gap-2">
                  {predefinedTags.map((tag) => {
                    const isSelected = selectedTags.includes(tag);
                    return (
                      <button
                        key={tag}
                        type="button"
                        onClick={() => handleTagToggle(tag)}
                        disabled={uploading}
                        className={`
                          inline-flex items-center px-3 py-2 rounded-full text-sm font-medium border
                          transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500/40
                          ${isSelected
                            ? 'bg-blue-600 text-white border-blue-500 shadow-lg shadow-blue-500/25'
                            : 'bg-gray-700 text-gray-300 border-gray-600 hover:bg-gray-600 hover:text-white hover:border-gray-500'
                          }
                          ${uploading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                        `}
                      >
                        {tag}
                        {isSelected && (
                          <span className="ml-2 text-xs">✓</span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
              
              {selectedTags.length > 0 && (
                <div className="mt-2">
                  <p className="text-xs text-gray-400 mb-2">
                    Tags seleccionados ({selectedTags.length}/{MAX_TAGS}):
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {selectedTags.map((tag) => (
                      <span
                        key={tag}
                        className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200"
                      >
                        {tag}
                        <button
                          type="button"
                          onClick={() => handleTagToggle(tag)}
                          className="ml-2 text-blue-600 hover:text-blue-800"
                          disabled={uploading}
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
        {/* Drop Zone */}
        <div
          {...getRootProps()}
          className={`
            border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors
            ${isDragActive 
              ? 'border-blue-500 bg-blue-900/20' 
              : 'border-gray-600 bg-gray-700/50 hover:border-gray-500'
            }
          `}
        >
          <input {...modifiedInputProps} />
          <FaCloudUploadAlt className="mx-auto text-4xl text-gray-400 mb-4" />
          {isDragActive ? (
            <p className="text-blue-400">Suelta los archivos aquí...</p>
          ) : (
            <div>
              <p className="text-gray-300 mb-2">
                Arrastra archivos aquí o haz clic para seleccionar
              </p>
              <p className="text-gray-500 text-sm">
                Máximo 500MB por archivo
              </p>
            </div>
          )}
        </div>

        {/* Files List */}
        {files.length > 0 && (
          <div className="space-y-2 max-h-60 overflow-y-auto">
            <h4 className="text-white font-medium">
              Archivos seleccionados ({files.length})
            </h4>
              {files.map((fileItem) => (
              <div
                key={fileItem.id}
                className={`
                  flex items-center justify-between p-3 rounded-lg border
                  ${getStatusColor(fileItem.status)}
                `}
              >
                <div className="flex items-center space-x-3 flex-1 min-w-0">
                  {getStatusIcon(fileItem.status)}
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-sm truncate" title={fileItem.filePath || fileItem.file.name}>
                      {fileItem.file.name}
                    </p>
                    {fileItem.hasFolderStructure && fileItem.filePath && (
                      <p className="text-blue-400 text-xs truncate" title={fileItem.filePath}>
                        📁 {fileItem.filePath.replace(currentPath, '').replace(/^\/+/, '')}
                      </p>
                    )}
                    <p className="text-gray-400 text-xs">
                      {formatFileSize(fileItem.file.size)}
                    </p>
                    {fileItem.status === 'uploading' && fileItem.progress !== undefined && (
                      <div className="mt-2 space-y-1">
                        {/* Barra de progreso */}
                        <div className="w-full bg-gray-700 rounded-full h-2 overflow-hidden">
                          <div 
                            className="bg-blue-500 h-full transition-all duration-300 ease-out"
                            style={{ width: `${fileItem.progress}%` }}
                          />
                        </div>
                        {/* Porcentaje y tiempo restante */}
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-blue-400 font-medium">
                            {Math.round(fileItem.progress)}%
                          </span>
                          {fileItem.timeRemaining > 0 ? (
                            <span className="text-gray-400">
                              Tiempo restante: {formatTimeRemaining(fileItem.timeRemaining)}
                            </span>
                          ) : fileItem.progress >= 90 && fileItem.progress < 100 ? (
                            <span className="text-gray-400 italic">
                              Procesando en servidor...
                            </span>
                          ) : null}
                        </div>
                      </div>
                    )}
                    {fileItem.error && (
                      <p className="text-red-400 text-xs mt-1">
                        {fileItem.error}
                      </p>
                    )}
                  </div>
                </div>
                
                {fileItem.status === 'pending' && (
                  <button
                    onClick={() => removeFile(fileItem.id)}
                    className="text-gray-400 hover:text-red-400 p-1"
                  >
                    <FaTimes />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-between items-center pt-4 border-t border-gray-700">
          <div className="text-sm text-gray-400">
            {files.length > 0 && (
              <span>
                {files.filter(f => f.status === 'success').length} de {files.length} completados
              </span>
            )}
          </div>
          
          <div className="flex space-x-3">
            <button
              onClick={handleClose}
              disabled={uploading}
              className="px-4 py-2 text-gray-300 border border-gray-600 rounded-lg hover:bg-gray-700 transition-colors disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              onClick={uploadFiles}
              disabled={files.length === 0 || uploading}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {uploading ? 'Subiendo...' : `Subir ${files.length} archivo(s)`}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default UploadModal; 